/**
 * CR-5880: Admin Setup -> Performance -> KPI Milestones.
 *
 * Key events ("Initial Launch", "ERP go-live", ...) drawn as dashed vertical
 * lines on the dashboard's OTIF and OTD graphs, so a change in performance can
 * be read against what happened. A group administrator adds, edits, deactivates
 * and deletes them; everyone else sees the list read-only. System milestones
 * cannot be deleted — they can be made inactive instead.
 */
import { useState } from 'react';
import type { KpiMilestone, KpiMilestoneInput } from '../../../lib/api';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Switch } from '../../ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../ui/dialog';
import { Flag, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { useKpiMilestones } from './useKpiMilestones';
import { APPLIES_TO_LABELS, MilestoneFormRow } from './MilestoneFormRow';

interface KpiMilestonesTabProps {
  /** Group administrators (and Helix) may change milestones. */
  canEdit: boolean;
}

const SYSTEM = 0;

export function KpiMilestonesTab({ canEdit }: KpiMilestonesTabProps) {
  const { formatDate } = useFormatters();
  const { milestones, loading, saving, create, update, remove } = useKpiMilestones();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<KpiMilestone | null>(null);

  const saveNew = async (data: KpiMilestoneInput) => {
    const ok = await create(data);
    if (ok) setAdding(false);
    return ok;
  };
  const saveEdit = (id: number) => async (data: KpiMilestoneInput) => {
    const ok = await update(id, data);
    if (ok) setEditingId(null);
    return ok;
  };
  const confirmDelete = async () => {
    if (!deleting) return;
    if (await remove(deleting.id)) setDeleting(null);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="pr-4">
          <CardTitle className="flex items-center gap-2">
            <Flag className="h-5 w-5" />
            KPI Milestones
          </CardTitle>
          <CardDescription>
            Key events drawn as dashed vertical lines on the dashboard&apos;s OTIF and OTD graphs, so a
            change in performance can be read against what happened.
          </CardDescription>
        </div>
        {canEdit && (
          <Button onClick={() => { setAdding(true); setEditingId(null); }} disabled={adding}>
            <Plus className="h-4 w-4 mr-2" />
            Add milestone
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {!canEdit && (
          <p className="text-sm text-muted-foreground mb-3">
            Only a group administrator can change milestones.
          </p>
        )}
        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Loading milestones...
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[34%]">Milestone</TableHead>
                <TableHead className="w-[18%]">Date</TableHead>
                <TableHead className="w-[18%]">Applies to</TableHead>
                <TableHead className="w-[10%]">Active</TableHead>
                <TableHead className="w-[20%] text-right">{canEdit ? 'Actions' : ''}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {adding && (
                <MilestoneFormRow saving={saving} onSave={saveNew} onCancel={() => setAdding(false)} />
              )}
              {milestones.length === 0 && !adding && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-6">
                    No milestones yet.{canEdit ? ' Add one to mark it on the OTIF and OTD graphs.' : ''}
                  </TableCell>
                </TableRow>
              )}
              {milestones.map((m) => (editingId === m.id ? (
                <MilestoneFormRow
                  key={m.id}
                  saving={saving}
                  initial={{ title: m.title, milestone_date: m.milestone_date, applies_to: m.applies_to, is_active: m.is_active }}
                  onSave={saveEdit(m.id)}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
                <TableRow key={m.id} className={m.is_active ? '' : 'text-muted-foreground'}>
                  <TableCell className="font-medium">
                    {m.title}
                    {m.milestone_type === SYSTEM && <Badge variant="secondary" className="ml-2">System</Badge>}
                  </TableCell>
                  <TableCell>{formatDate(m.milestone_date)}</TableCell>
                  <TableCell>{APPLIES_TO_LABELS[m.applies_to]}</TableCell>
                  <TableCell>
                    <Switch
                      aria-label={`${m.title} active`}
                      checked={m.is_active}
                      disabled={!canEdit || saving}
                      onCheckedChange={(checked) => update(m.id, { is_active: checked })}
                    />
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {canEdit && (
                      <>
                        <Button size="sm" variant="ghost" aria-label={`Edit ${m.title}`}
                          onClick={() => { setEditingId(m.id); setAdding(false); }}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" aria-label={`Delete ${m.title}`}
                          disabled={m.milestone_type === SYSTEM}
                          title={m.milestone_type === SYSTEM ? 'System milestones cannot be deleted — make it inactive to hide it' : undefined}
                          onClick={() => setDeleting(m)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              )))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={deleting !== null} onOpenChange={(open) => { if (!open) setDeleting(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete milestone?</DialogTitle>
            <DialogDescription>
              &ldquo;{deleting?.title}&rdquo; will no longer appear on the OTIF and OTD graphs. To hide it
              without losing it, make it inactive instead.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)} disabled={saving}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
